import React, { useEffect, useMemo, useState } from "react";

import { Tabs, Button, Row } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import RoutePoints from "./components/RoutePoints";
import Routes from "./components/Routes";
import Assignments from "./components/Assignments";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllEmployees, fetchHrLocation } from "../AttendanceOverview/attendanceOverviewThunk";
import { selectEmployees, selectHrLocation } from "../AttendanceOverview/attendanceOverviewSelectors";
import LiveTracking from "./components/LiveTracking";
import RouteHistory from "./components/RouteHistory";
import RouteExecution from "./components/RouteExecution";
import Analytics from "./components/Analytics";
import { selectAuthInfo } from "../Auth/authSelectors";
import { accessChecker } from "../../lib/functions";
import { fetchRouteAreas, fetchRouteAssignments } from "./workRoutesThunk";
import Areas from "./components/Areas";
import MyRouteSchedule from "./components/MyRouteSchedule";


const WorkRoutes = () => {
  const navigate = useNavigate();
  const authInfo = useSelector(selectAuthInfo);
  const [isAdminUser, isAdminUserSet] = useState(false);
  const [mapCenter, setMapCenter] = useState(null);
  const isEmployee =
    authInfo?.employee_id &&
    authInfo.employee_id !== 0;
  const dispatch = useDispatch();
  const rawEmployees = useSelector(selectEmployees);
  const hrLocation = useSelector(selectHrLocation);

  useEffect(() => {
    const hrRoles = [
      // HR role
      "ForthERP_Basic_hr",
      "ForthERP_Standard_hr",
      "ForthERP_Pro_hr",
      "PharmaERP_Basic_hr",
      "PharmaERP_Standard_hr",
      "PharmaERP_Pro_hr",
      "PharmaBooks_Basic_hr",
      "PharmaBooks_Standard_hr",
      "PharmaBooks_Pro_hr",
      "ForthBooks_Basic_hr",
      "ForthBooks_Standard_hr",
      "ForthBooks_Pro_hr",
      "HRPayroll_Standard_hr",
      "HRPayroll_Pro_hr",
      "SuperERP_hr",
      'ForthCRM_Pro_hr',
      'ForthCRM_Standard_hr',

      // Admin role
      "ForthERP_Basic_admin",
      "ForthERP_Standard_admin",
      "ForthERP_Pro_admin",
      "PharmaERP_Basic_admin",
      "PharmaERP_Standard_admin",
      "PharmaERP_Pro_admin",
      "PharmaBooks_Basic_admin",
      "PharmaBooks_Standard_admin",
      "PharmaBooks_Pro_admin",
      "ForthBooks_Basic_admin",
      "ForthBooks_Standard_admin",
      "ForthBooks_Pro_admin",
      "HRPayroll_Standard_admin",
      "HRPayroll_Pro_admin",
      "SuperERP_admin",
      "SuperERP_super_admin",
      "ForthCRM_Standard_admin",
      "ForthCRM_Pro_admin",
    ];

    const userRoles = authInfo.userInfo.user_role_names
      ? authInfo.userInfo.user_role_names.split(",").map((role) => role.trim())
      : [];

    const isAdminHR = userRoles.some((role) => hrRoles.includes(role));

    isAdminUserSet(isAdminHR);
  }, [authInfo]);

  useEffect(() => {
    dispatch(fetchHrLocation());
  }, [dispatch]);

  useEffect(() => {
    if (
      hrLocation?.latitude &&
      hrLocation?.longitude
    ) {
      setMapCenter({
        lat: Number(hrLocation.latitude),
        lng: Number(hrLocation.longitude),
      });

      return;
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setMapCenter({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          });
        },

        () => {
          setMapCenter({
            lat: 20,
            lng: 0,
          });
        }
      );
    } else {
      setMapCenter({
        lat: 20,
        lng: 0,
      });
    }
  }, [hrLocation]);




  const employees = rawEmployees.map((emp) => ({
    ...emp,
    employee_id: emp.id,
    employee_name: emp.user_name,
  }));

  useEffect(() => {
    dispatch(fetchAllEmployees());
    dispatch(fetchRouteAssignments());
    dispatch(fetchRouteAreas());
  }, [dispatch])

  const items = [];

  if (isEmployee && !isAdminUser) {
    items.push({
      key: "route-execution",
      label: "My Route",
      children: <RouteExecution center={mapCenter || { lat: 20, lng: 0, }}
        zoom={mapCenter ? 10 : 2} />,
    },
      {
        key: "my-schedule",
        label: "My Schedule",
        children: <MyRouteSchedule />,
      },
      {
        key: "history",
        label: "My History",
        children: (
          <RouteHistory
            center={mapCenter || { lat: 20, lng: 0 }}
            zoom={mapCenter ? 10 : 2}
            isEmployee={true}
            employeeId={authInfo.employee_id}
          />
        ),
      }
    );
  }
  if (isAdminUser) {
    items.push(
      {
        key: "route-points",
        label: "Route Points",
        children: <RoutePoints
          center={mapCenter || { lat: 20, lng: 0, }}
          zoom={mapCenter ? 10 : 2} />,
      },

      {
        key: "areas",
        label: "Areas",
        children: <Areas />,
      },

      {
        key: "routes",
        label: "Routes",
        children: <Routes
          center={mapCenter || { lat: 20, lng: 0, }}
          zoom={mapCenter ? 10 : 2} />,
      },

      {
        key: "assignments",
        label: "Assignments",
        children: (
          <Assignments
            employees={employees}
          />
        ),
      },

      {
        key: "live-tracking",
        label: "Live Tracking",
        children: (
          <LiveTracking
            employees={employees}
            center={mapCenter || { lat: 20, lng: 0, }}
            zoom={mapCenter ? 10 : 2}
          />
        ),
      },

      {
        key: "history",
        label: "History & Report",
        children: <RouteHistory
          center={mapCenter || { lat: 20, lng: 0, }}
          zoom={mapCenter ? 10 : 2}
          isEmployee={false}
          employeeId={null} />
      },

      {
        key: "analytics",
        label: "Analytics",
        children: <Analytics />,
      }
    );
  }

  return (
    <div>
      <Row style={{ marginBottom: 10 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined style={{ fontSize: 18 }} />}
          onClick={() => navigate(-1)}
          data-testid="work-routes-back-button"
        />
      </Row>
      <Tabs
        defaultActiveKey={
          isEmployee && !isAdminUser
            ? "route-execution"
            : "route-points"
        }
        items={items}
      />
    </div>
  );
};

export default WorkRoutes;